import { API_BASE_URL } from '../constants/api';

/**
 * Helper: authenticated fetch that sends HttpOnly cookie automatically.
 */
async function authFetch(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

class UserManagementService {
  /**
   * Get all users with pagination and filtering
   */
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/users?${queryString}` : '/users';
    return authFetch(`${API_BASE_URL}${url}`);
  }

  /**
   * Get single user by ID
   */
  async getUserById(userId) {
    return authFetch(`${API_BASE_URL}/users/${userId}`);
  }

  /**
   * Create new user
   */
  async createUser(userData) {
    return authFetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Update user
   */
  async updateUser(userId, userData) {
    return authFetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Update user status
   */
  async updateUserStatus(userId, status) {
    return authFetch(`${API_BASE_URL}/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  /**
   * Delete user
   */
  async deleteUser(userId) {
    return authFetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Send password reset email to user
   */
  async sendPasswordReset(userId) {
    return authFetch(`${API_BASE_URL}/users/${userId}/reset-password`, {
      method: 'POST',
    });
  }

  /**
   * Unlock a locked user account
   */
  async unlockUser(userId) {
    return authFetch(`${API_BASE_URL}/users/${userId}/unlock`, {
      method: 'POST',
    });
  }

  /**
   * Turn Deployed Hearts (HCW app) access on or off for a user.
   * details: { enabled, firstName, lastName, phone, professionalRole,
   *            professionalRoleOther, sendEmail, sendSms }
   */
  async setHcwAccess(userId, details) {
    return authFetch(`${API_BASE_URL}/users/${userId}/hcw-access`, {
      method: 'PUT',
      body: JSON.stringify(details),
    });
  }

  /**
   * Turn Deployed Hearts access on for several users at once.
   */
  async bulkEnableHcwAccess(userIds, { sendEmail = true, sendSms = true } = {}) {
    return authFetch(`${API_BASE_URL}/users/hcw-access/bulk`, {
      method: 'POST',
      body: JSON.stringify({ userIds, sendEmail, sendSms }),
    });
  }

  /**
   * Re-send the Deployed Hearts invite email/text.
   */
  async resendHcwInvite(userId, { sendEmail = true, sendSms = true } = {}) {
    return authFetch(`${API_BASE_URL}/users/${userId}/hcw-access/invite`, {
      method: 'POST',
      body: JSON.stringify({ sendEmail, sendSms }),
    });
  }
}

export default new UserManagementService();
